import type { ColScaffold, RowScaffold, ScaffoldHeaderCellSpans } from "./interface";

/**
 * Rendering mode for a single scaffold cell as determined by the active span definitions.
 *
 * - `"normal"` – Render with standard dimensions.
 * - `"spanning"` – Render with effective (merged) width and/or height.
 * - `"covered-same-row"` – Do not render; this cell is absorbed by a colSpan in the same row.
 * - `"covered-other-row"` – Render as an invisible placeholder so flex layout is preserved;
 *   this cell is overlapped by a rowSpan from an earlier row.
 */
export type HeaderCellRenderMode = "normal" | "spanning" | "covered-same-row" | "covered-other-row";

/**
 * Computed rendering information for a single scaffold cell, derived from the active span
 * definitions.
 */
export interface HeaderCellRenderInfo {
  /** How this cell should be rendered given the active spans. */
  mode: HeaderCellRenderMode;
  /**
   * Effective pixel width of the cell, including all spanned columns.
   * Only set when `mode === "spanning"` and the cell has a colSpan > 1.
   */
  effectiveWidth?: number;
  /**
   * Effective pixel height of the cell, including all spanned rows.
   * Only set when `mode === "spanning"` and the cell has a rowSpan > 1.
   */
  effectiveHeight?: number;
  /** True when the cell has an active rowSpan; used to apply overflow-visible CSS on the row. */
  hasRowSpan: boolean;
}

/**
 * Pre-computed span rendering map, keyed by rowId then colId.
 * Cells not present in this map should be rendered normally.
 */
export type HeaderSpanMap = Record<string, Record<string, HeaderCellRenderInfo>>;

/**
 * Computes the per-cell rendering information for all cells affected by the provided span
 * definitions.  Cells not mentioned in `spans` are not included in the returned map and should
 * be rendered normally.
 *
 * Columns are ordered by their `colNumber`; rows within each row-group are ordered by their
 * `rowNumber`.  A `colSpan` of N absorbs the next N−1 columns to the right within the same row.
 * A `rowSpan` of N absorbs the next N−1 rows below within the same row-group.
 *
 * @param rows - All row definitions currently active in the scaffold.
 * @param cols - All column definitions currently active in the scaffold.
 * @param spans - Optional span definitions.  When undefined or empty the returned map is empty.
 * @param previewColWidths - Transient column-width overrides used during resize drags.
 * @param previewRowHeights - Transient row-height overrides used during resize drags.
 * @returns A map of per-cell rendering information for every cell touched by a span definition.
 */
export function computeHeaderSpanState<T>(
  rows: RowScaffold<T>[],
  cols: ColScaffold<T>[],
  spans: ScaffoldHeaderCellSpans | undefined,
  previewColWidths: Record<string, number>,
  previewRowHeights: Record<string, number>,
): HeaderSpanMap {
  if (!spans) {
    return {};
  }

  const orderedCols = [...cols].sort((a, b) => a.colNumber - b.colNumber);

  /** Rows grouped by rowGroupNumber, each group sorted by rowNumber. */
  const rowsByGroup = rows.reduce(
    (acc, row) => {
      if (!acc[row.rowGroupNumber]) {
        acc[row.rowGroupNumber] = [];
      }
      acc[row.rowGroupNumber].push(row);
      return acc;
    },
    {} as Record<number, RowScaffold<T>[]>,
  );

  for (const group of Object.values(rowsByGroup)) {
    group.sort((a, b) => a.rowNumber - b.rowNumber);
  }

  const result: HeaderSpanMap = {};

  /**
   * Stores cells that are covered by a spanning cell from the same row (colSpan) or a
   * previous row (rowSpan). Keyed as "rowId:colId".
   * Value "same-row" means col-span covered; "other-row" means row-span covered.
   */
  const coveredCells = new Map<string, "same-row" | "other-row">();

  for (const row of rows) {
    const rowSpanDefs = spans[row.id];
    if (!rowSpanDefs) {
      continue;
    }

    for (const col of orderedCols) {
      const span = rowSpanDefs[col.id];
      if (!span) {
        continue;
      }

      const colSpan = span.colSpan ?? 1;
      const rowSpan = span.rowSpan ?? 1;

      if (colSpan <= 1 && rowSpan <= 1) {
        continue;
      }

      const colIndex = orderedCols.indexOf(col);
      /**
       * `rowsByGroup` is built from the same `rows` array we iterate over, so every
       * `row.rowGroupNumber` is guaranteed to be a key in the map.  The fallback is
       * unreachable but kept as a defensive default.
       */
      const groupRows =
        rowsByGroup[row.rowGroupNumber] ??
        // istanbul ignore next – rowsByGroup is populated from the same rows array, so the key always exists
        [];
      const rowIndex = groupRows.indexOf(row);

      /** Effective pixel width: sum of the spanned columns' widths. */
      let effectiveWidth: number | undefined;
      if (colSpan > 1) {
        effectiveWidth = 0;
        for (let i = colIndex; i < Math.min(colIndex + colSpan, orderedCols.length); i++) {
          effectiveWidth += previewColWidths[orderedCols[i].id] ?? orderedCols[i].width;
        }
      }

      /** Effective pixel height: sum of the spanned rows' heights. */
      let effectiveHeight: number | undefined;
      if (rowSpan > 1) {
        effectiveHeight = 0;
        for (let j = rowIndex; j < Math.min(rowIndex + rowSpan, groupRows.length); j++) {
          effectiveHeight += previewRowHeights[groupRows[j].id] ?? groupRows[j].height;

          /** Mark cells in subsequent rows as covered (invisible placeholder). */
          if (j > rowIndex) {
            for (let k = colIndex; k < Math.min(colIndex + colSpan, orderedCols.length); k++) {
              const key = `${groupRows[j].id}:${orderedCols[k].id}`;
              if (!coveredCells.has(key)) {
                coveredCells.set(key, "other-row");
              }
            }
          }
        }
      }

      /** Mark same-row cells absorbed by this colSpan as not-rendered. */
      for (let k = colIndex + 1; k < Math.min(colIndex + colSpan, orderedCols.length); k++) {
        const key = `${row.id}:${orderedCols[k].id}`;
        if (!coveredCells.has(key)) {
          coveredCells.set(key, "same-row");
        }
      }

      if (!result[row.id]) {
        result[row.id] = {};
      }
      result[row.id][col.id] = {
        mode: "spanning",
        effectiveWidth,
        effectiveHeight,
        hasRowSpan: rowSpan > 1,
      };
    }
  }

  /** Write the covered-cell entries into the result map. */
  for (const [cellKey, coverType] of coveredCells) {
    const colonIndex = cellKey.indexOf(":");
    const rowId = cellKey.slice(0, colonIndex);
    const colId = cellKey.slice(colonIndex + 1);

    if (!result[rowId]) {
      result[rowId] = {};
    }
    result[rowId][colId] = {
      mode: coverType === "same-row" ? "covered-same-row" : "covered-other-row",
      hasRowSpan: false,
    };
  }

  return result;
}
